import { useState, useRef } from 'react'
import { Upload, FileImage, X } from 'lucide-react'

export interface FileUploadProps {
  accept?: string
  onFileSelect?: (file: File | null) => void
  selectedFile?: File | null
  disabled?: boolean
  label?: string
  showPreview?: boolean
  className?: string
  error?: string
  maxSizeMB?: number
}

export function FileUpload({
  accept,
  onFileSelect,
  selectedFile: externalFile,
  disabled = false,
  label,
  showPreview = false,
  className = '',
  error,
  maxSizeMB = 5
}: FileUploadProps) {
  const [internalFile, setInternalFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [sizeError, setSizeError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentFile = externalFile !== undefined ? externalFile : internalFile

  const processFile = (file: File) => {
    if (file) {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setSizeError(`O arquivo deve ter no máximo ${maxSizeMB} MB.`)
        return
      }
      setSizeError(null)

      if (externalFile === undefined) {
        setInternalFile(file)
      }
      
      if (showPreview && file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreview(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setPreview(null)
      }
      
      onFileSelect?.(file)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (file) {
      processFile(file)
    }
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      // Verificar se o tipo de arquivo é aceito
      if (accept) {
        const acceptedTypes = accept.split(',').map(type => type.trim())
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
        const fileType = file.type
        
        const isAccepted = acceptedTypes.some(type => {
          if (type.startsWith('.')) {
            return type.toLowerCase() === fileExtension
          }
          return fileType.match(type.replace('*', '.*'))
        })

        if (!isAccepted) {
          return
        }
      }
      
      processFile(file)
    }
  }

  const handleRemove = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    
    if (externalFile === undefined) {
      setInternalFile(null)
    }
    
    setPreview(null)
    setSizeError(null)
    onFileSelect?.(null)
  }

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  const fileName = currentFile?.name || 'Nenhum arquivo selecionado'

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`
          flex flex-col items-center justify-center
          border-2 border-dashed rounded-lg
          cursor-pointer transition-all duration-200
          ${error ? 'border-red-300 bg-red-50' : isDragging ? 'border-brand-600 bg-brand-50' : 'border-brand-500 bg-white hover:bg-gray-50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${preview || currentFile ? 'h-auto min-h-[200px] p-6' : 'h-[200px] p-6'}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />

        {preview ? (
          <div className="flex flex-col items-center space-y-4">
            <img
              src={preview}
              alt="Preview"
              className="max-w-full max-h-48 rounded-lg object-contain"
            />
            <p className="text-sm text-gray-600 font-medium">{fileName}</p>
          </div>
        ) : currentFile ? (
          <div className="flex flex-col items-center space-y-4">
            <FileImage className="w-16 h-16 text-brand-500" />
            <p className="text-sm text-gray-600 font-medium">{fileName}</p>
          </div>
        ) : (
          <>
            <Upload className="w-16 h-16 text-brand-500" />
            <p className="mt-4 text-sm text-gray-600 font-medium">
              Clique para selecionar ou arraste o arquivo aqui
            </p>
          </>
        )}
      </div>

      {currentFile && (
        <div className="mt-3 flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <FileImage className="w-5 h-5 text-brand-500 flex-shrink-0" />
            <span className="text-sm text-gray-700 truncate flex-1">
              {fileName}
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemove()
              }}
              className="ml-3 p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-all flex-shrink-0"
              title="Remover arquivo"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {(error || sizeError) && (
        <p className="mt-1 text-sm text-red-600">{sizeError || error}</p>
      )}
    </div>
  )
}

